/**
 * In welche Richtung gerade **gepannt** wird — und welcher **Randpfeil**
 * deshalb aufleuchtet. Siehe „Randpfeil" in CONTEXT.md.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`: Die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein.
 */

import type { OffscreenDirections } from './canvasOffscreen'

/** Die acht Richtungen — dieselben Namen wie in {@link OffscreenDirections}. */
export type PanDirection = keyof OffscreenDirections

/**
 * Der Einheitsvektor je Richtung, in BILDSCHIRM-Koordinaten: y zeigt nach
 * unten. Die Diagonalen sind nicht normiert (Länge √2) — für den Vergleich
 * zweier Nachbarn untereinander genügt das, weil dort nie eine Achse gegen
 * eine Diagonale antritt.
 */
const VECTORS: Record<PanDirection, readonly [number, number]> = {
  left: [-1, 0],
  right: [1, 0],
  top: [0, -1],
  bottom: [0, 1],
  topLeft: [-1, -1],
  topRight: [1, -1],
  bottomLeft: [-1, 1],
  bottomRight: [1, 1],
}

/**
 * Die beiden Nachbarn je Richtung — je EINEN Sektor entfernt.
 *
 * Zu einer Diagonale sind das ihre beiden Achsen, zu einer Achse ihre beiden
 * Diagonalen. Weiter wird nie ausgewichen: Ein Pfeil zwei Sektoren neben der
 * Fahrtrichtung hätte mit ihr nichts mehr zu tun.
 */
const NEIGHBOURS: Record<PanDirection, readonly [PanDirection, PanDirection]> = {
  left: ['topLeft', 'bottomLeft'],
  right: ['topRight', 'bottomRight'],
  top: ['topLeft', 'topRight'],
  bottom: ['bottomLeft', 'bottomRight'],
  topLeft: ['top', 'left'],
  topRight: ['top', 'right'],
  bottomLeft: ['bottom', 'left'],
  bottomRight: ['bottom', 'right'],
}

/**
 * Ab wann eine Bewegung als schräg gilt: `tan(30°)`.
 *
 * **Die Achsen bekommen 60°, die Diagonalen 30°** — und nicht acht gleiche
 * Achtel. Man zieht selten exakt gerade; bei gleich großen Sektoren spränge
 * eine „eigentlich waagerechte" Bewegung schon bei 22,5° auf die Diagonale und
 * die Ecken fingen die Hälfte aller Gesten.
 */
const DIAGONAL_RATIO = Math.tan(Math.PI / 6)

/**
 * Wie weit sich der Ausschnitt je Auswertung mindestens bewegt haben muss.
 *
 * Darunter gibt es keine Richtung: Ein ruhender Zeiger meldet trotzdem winzige
 * Schwankungen, und aus denen eine Fahrtrichtung abzuleiten hieße, den Pfeil
 * würfeln zu lassen.
 */
const MIN_STEP = 0.35

/**
 * Der Sektor, in den dieser Schritt fällt — oder `null`, wenn er zu klein ist.
 *
 * @param dx Bewegung des AUSSCHNITTS nach rechts (nicht der Hand)
 * @param dy Bewegung des Ausschnitts nach unten
 */
export function panSector(dx: number, dy: number): PanDirection | null {
  const ax = Math.abs(dx)
  const ay = Math.abs(dy)
  if (Math.hypot(dx, dy) < MIN_STEP) return null

  // Ohne Trigonometrie: Die Achse gewinnt, solange die kleinere Komponente
  // unter `tan(30°)` der größeren liegt. Dieselbe Aussage wie „Diagonale ab
  // 30°", nur als ein Vergleich statt eines `atan2`.
  if (ay < ax * DIAGONAL_RATIO) return dx > 0 ? 'right' : 'left'
  if (ax < ay * DIAGONAL_RATIO) return dy > 0 ? 'bottom' : 'top'

  if (dy < 0) return dx > 0 ? 'topRight' : 'topLeft'
  return dx > 0 ? 'bottomRight' : 'bottomLeft'
}

/**
 * Welcher Randpfeil leuchtet: der des Sektors, wenn es ihn gibt — sonst der
 * nächstliegende Nachbar, den es gibt.
 *
 * **Immer höchstens einer.** Stehen beide Nachbarn zur Wahl, gewinnt der,
 * dessen Richtung dem tatsächlichen Vektor näher liegt (größeres
 * Skalarprodukt). Damit bleibt die Antwort eindeutig, statt zwei Pfeile
 * gleichzeitig aufleuchten zu lassen.
 *
 * @param sector    das Ergebnis von {@link panSector}
 * @param available welche Pfeile überhaupt stehen
 * @param dx        derselbe Vektor wie oben — entscheidet zwischen zwei Nachbarn
 * @param dy
 */
export function highlightedHint(
  sector: PanDirection | null,
  available: OffscreenDirections,
  dx: number,
  dy: number,
): PanDirection | null {
  if (!sector) return null
  if (available[sector]) return sector

  // Ausgewichen wird nur, wenn es der Bewegung überhaupt entspricht: Ein
  // Nachbar, in dessen Richtung man gar nicht fährt (negatives Skalarprodukt),
  // wäre eine falsche Auskunft.
  let best: PanDirection | null = null
  let bestScore = 0

  for (const candidate of NEIGHBOURS[sector]) {
    if (!available[candidate]) continue
    const [vx, vy] = VECTORS[candidate]
    const score = dx * vx + dy * vy
    if (score > bestScore) {
      best = candidate
      bestScore = score
    }
  }

  return best
}
